import { useMemo, useState } from "react";
import { formatRowRanges, groupWarnings, type TWarningGroup } from "./groupWarnings";

export interface TWarningsPanelProps {
    warnings: string[];
    /** Warnings shown before the "Show N more" toggle. */
    maxVisible?: number;
    /** Row numbers/ranges listed per grouped warning before its "show all" toggle. */
    maxRowsPreview?: number;
}

function WarningRows({ group, maxRowsPreview }: { group: TWarningGroup; maxRowsPreview: number }) {
    const [expanded, setExpanded] = useState(false);
    const ranges = useMemo(() => formatRowRanges(group.rows), [group.rows]);
    const hidden = ranges.length - maxRowsPreview;
    const shown = expanded || hidden <= 0 ? ranges : ranges.slice(0, maxRowsPreview);

    return (
        <span className="block pl-5 text-xs text-amber-800/70">
            Rows {shown.join(", ")}
            {hidden > 0 && (
                <>
                    {!expanded && ` and ${hidden} more`}{" "}
                    <button
                        type="button"
                        className="font-medium underline underline-offset-2 hover:text-amber-900"
                        onClick={() => setExpanded((v) => !v)}
                    >
                        {expanded ? "show less" : "show all"}
                    </button>
                </>
            )}
        </span>
    );
}

/**
 * Amber warnings panel for the review page. Per-row warnings that share a reason
 * are grouped ("Skipped 63 rows: …") with their row numbers listed compactly, and
 * only the first few warnings are shown until the user expands the list.
 */
export function WarningsPanel({
    warnings,
    maxVisible = 5,
    maxRowsPreview = 15,
}: TWarningsPanelProps) {
    const [showAll, setShowAll] = useState(false);
    const groups = useMemo(() => groupWarnings(warnings), [warnings]);
    if (groups.length === 0) return null;

    const hidden = groups.length - maxVisible;
    const visible = showAll || hidden <= 0 ? groups : groups.slice(0, maxVisible);

    return (
        <div
            className="rounded-lg border border-amber-200 bg-amber-50/80 p-4 text-sm"
            data-testid="file-warnings"
        >
            <p className="font-medium text-amber-800">
                Warnings{warnings.length > 1 && ` (${warnings.length})`}
            </p>
            <ul className="mt-1 list-disc list-inside space-y-0.5 text-amber-800/90">
                {visible.map((group, i) => (
                    <li key={i}>
                        {group.message}
                        {group.rows.length > 0 && (
                            <WarningRows group={group} maxRowsPreview={maxRowsPreview} />
                        )}
                    </li>
                ))}
            </ul>
            {hidden > 0 && (
                <button
                    type="button"
                    className="mt-2 text-xs font-medium text-amber-800 underline underline-offset-2 hover:text-amber-900"
                    onClick={() => setShowAll((v) => !v)}
                >
                    {showAll ? "Show fewer warnings" : `Show ${hidden} more warnings`}
                </button>
            )}
        </div>
    );
}
